import * as React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import Announcement from '@/components/Announcement/Announcement';
import Cookies from 'universal-cookie';
import CountriesWithISOCodes from '@liveauctioneers/caterwaul-components/lib/Intl/CountriesWithISOCodes';
import { FormattedMessage } from 'react-intl';
import { getCatalog } from '@/redux/modules/catalog';
import { getCatalogDetail } from '@/redux/modules/catalogDetail';
import { getHasRequiredDimensions } from '@/redux/modules/itemDetail';
import { getItem } from '@/redux/modules/item';
import { getSellerInArtaBeta } from '@/redux/modules/seller';
import { getShippingEstimate } from '@/redux/modules/shipping';
import { getUserData } from '@/redux/modules/user';
import isEqual from 'react-fast-compare';
import LocalPickupLine from './LocalPickupLine';
import { openShippingAndPaymentModal } from '@/redux/modules/modal';
import ShippingCostLine from './ShippingCostLine';
import ShippingToLine from './ShippingToLine';
import ShipsFromLine from './ShipsFromLine';
import styled from 'styled-components';
import { StyledActionText } from './helpers/styles';
import Text from '@liveauctioneers/caterwaul-components/lib/Text/Text';

const cookie = new Cookies();

type Props = {
    itemId: number;
};

const JASPER52_ID = 5584;

/**
 * Displays ARTA shipping cost estimates for auction items
 */
const ShippingInfo = ({ itemId }: Props) => {
    const { catalogId } = useSelector((state) => getItem(state, itemId), isEqual);
    const { address: catalogAddress, sellerId } = useSelector((state) => getCatalog(state, catalogId), isEqual);
    const { freeLocalPickup } = useSelector((state) => getCatalogDetail(state, catalogId), isEqual);
    const hasArtaShipping = useSelector((state) => getSellerInArtaBeta(state, sellerId), isEqual);
    const hasRequiredDimensions = useSelector((state) => getHasRequiredDimensions(state, itemId), isEqual);
    const dispatch = useDispatch();
    const isJasper52 = sellerId === JASPER52_ID;

    const shippingEstimate = useSelector((state) => getShippingEstimate(state, itemId), isEqual);
    const hasError = shippingEstimate === 'error';

    const { country, state, zip: userDataZip } = useSelector(getUserData, isEqual);
    const zip = userDataZip || cookie.get('bidder-zip');

    const { code: countryCode = 'US' } =
        CountriesWithISOCodes.find((currCountry) => currCountry?.name === country || currCountry?.code === country) ||
        {};
    const locationIsKnown = countryCode && zip;
    const [showShippingToLocation, setShowShippingToLocation] = React.useState(locationIsKnown);
    // @ts-ignore type mismatch, have to dig to fix
    const [shippingToLocation, setShippingToLocation]: [{ countryCode: string; zip: string }, any] = React.useState({
        countryCode,
        zip,
    });

    React.useEffect(() => setShippingToLocation({ countryCode, zip }), [countryCode, zip]);
    React.useEffect(() => setShowShippingToLocation(locationIsKnown), [locationIsKnown]);

    const openShipAndPayModal = React.useCallback(() => dispatch(openShippingAndPaymentModal()), [dispatch]);

    return (
        <ShippingInfoContainer>
            <HeaderText h4 primary>
                <FormattedMessage id="shippingInfo.sectionHeader" />
            </HeaderText>
            <ZipCodeContainer>
                {hasArtaShipping && !hasError && hasRequiredDimensions && (
                    <ShippingToLine
                        itemId={itemId}
                        setShippingToLocation={setShippingToLocation}
                        setShowShippingToLocation={setShowShippingToLocation}
                        shippingToLocation={shippingToLocation}
                        showShippingToLocation={showShippingToLocation}
                    />
                )}
            </ZipCodeContainer>
            <ShippingInfoLine>
                <ShippingCostLine
                    catalogId={catalogId}
                    country={shippingToLocation.countryCode}
                    itemId={itemId}
                    onClick={openShipAndPayModal}
                    sellerId={sellerId}
                    state={state}
                    zip={shippingToLocation.zip}
                />
            </ShippingInfoLine>
            {catalogAddress?.country && catalogAddress?.city && (
                <>
                    {isJasper52 ? (
                        <ShippingInfoLineJasper>
                            <ShipsFromLine catalogId={catalogId} itemId={itemId} />
                        </ShippingInfoLineJasper>
                    ) : (
                        <ShippingInfoLine>
                            <ShipsFromLine catalogId={catalogId} itemId={itemId} />
                        </ShippingInfoLine>
                    )}
                    {/**
                     * TODO: remove freeLocalPickup check
                     * @see https://liveauctioneers.atlassian.net/browse/FPM-3353
                     */}
                    {freeLocalPickup && (
                        <ShippingInfoLine>
                            <LocalPickupLine catalogId={catalogId} />
                        </ShippingInfoLine>
                    )}
                </>
            )}
            <Announcement catalogId={catalogId} />
            <StyledActionText onClick={openShipAndPayModal}>
                <FormattedMessage id="shippingInfo.viewPoliciesLink" />
            </StyledActionText>
        </ShippingInfoContainer>
    );
};

export default React.memo<Props>(ShippingInfo);

const ShippingInfoContainer = styled.div`
    border-bottom-style: solid;
    border-bottom-width: 1px;
    border-bottom-color: ${({ theme }) => theme.colors.grey400};
    margin: 20px 0;
    padding-bottom: 20px;

    @media (max-width: 500px) {
        border-bottom: none;
        padding-bottom: 5px;
    }
`;

const ZipCodeContainer = styled.div`
    display: flex;
    flex-direction: column;
    justify-content: center;
    && {
        > * {
            margin-bottom: 10px;
            &:last-child {
                margin-bottom: 0;
            }
        }
    }
`;

const ShippingInfoLine = styled.div`
    display: flex;
    flex-wrap: nowrap;
    align-items: center;
    margin: 20px 0;
`;

const ShippingInfoLineJasper = styled.div`
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    margin: 20px 0;
`;

const HeaderText = styled(Text)`
    && {
        font-family: ${({ theme }) => theme.typography.fontPrimaryStrong};
        font-size: 16px;
        letter-spacing: 0.5px;
    }
`;
